import type { MouseEventHandler } from 'react'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import clsx from 'clsx'
import { SearchMenuStates } from '../type'
import DateInputDesktop from './SearchDateInput/SearchDateInputDesktop'
import SearchTravellersInputDesktop from './SearchTravellersInput/SearchTravellersInputDesktop'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import type { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import config from 'constants/config'
import SearchLocationInput from './SearchLocationInput/SearchLocationInput'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import NavigationHeader from 'components/App/Header/HeaderMain/NavigationHeader/NavigationHeader'
import Group from 'components/utils/Group'

import MapSearchNav from 'components/NavKit/MapSearchNav'
import { NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS } from 'constants/naturalLanguageSearch'

const InputWrapper = styled.section`
  padding: 0;

  ${mediaQueryUp.tablet} {
    width: 100%;
    max-width: ${rem(1140)};
    position: relative;
    gap: ${rem(12)};
    display: grid;
    grid-template:
      "location-input location-input location-input" auto
      "date-input traveller-input actions" auto;
  }

  ${mediaQueryUp.desktop} {
    grid-template: "location-input date-input traveller-input actions" auto / 1fr ${rem(320)} minmax(${rem(154)}, auto) auto;
  }
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  &.location-input {
    grid-area: location-input;
  }

  &.date-input {
    grid-area: date-input;
  }

  &.traveller-input {
    grid-area: traveller-input;
  }

  &.actions {
    grid-area: actions;
  }

  &.arrow {
    grid-area: arrow;
    display: flex;
    align-items: flex-start;
  }

  &.filters {
    grid-area: filters;
    display: flex;
    align-items: flex-end;
  }

  &.login {
    grid-area: login;
  }
`

const SearchButton = styled(TextButton)`
  height: ${rem(50)};
  min-width: ${rem(112)};
`

interface MappedStateProps {
  isLoggedIn: boolean
}

interface Props {
  searchItem: App.SearchItem
  rooms: Array<App.Occupants>
  openDatePickerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
  openTravellerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
  openLocationMenu: (e?: React.MouseEvent<HTMLButtonElement>) => void
  closeMenu: () => void
  activeMenu: SearchMenuStates
  onRoomChange: (rooms: Array<App.Occupants>) => void
  onRoomsApply: () => void
  onDatesApply: () => void
  onDatesDecline: () => void
  showRoomErrors: boolean
  anytimeDateSelected?: boolean
  selectAnytimeDate?: (shouldApply: boolean) => void
  selectSpecificDates?: () => void
  selectFlexibleDates?: () => void
  onFlexibleMonthSelected?: () => void
  onFlexibleNightsSelected?: () => void
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  onLocationChange?: (item: App.SearchItem) => void
  saleUnit?: string
  typeaheadTypes?: Array<App.SearchPlaceType>
  recentSearches?: Array<App.FullRecentSearch>
  switchView?: {
    label: string
    view: 'map' | 'list'
    URL: string
    goBack?: boolean
  }
  locationSearchPlaceholder?: string
  updateSearchSessionId?: (newSessionId?: string) => void
  label?: string
  showNaturalLanguageSearchSuggestions?: boolean
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  /** Called when user manually selects dates (not AI-extracted) */
  onUserSelectedDates?: (dates: App.TypeaheadExtractedTravelDates) => void
  /** Called when user manually selects occupancy (not AI-extracted) */
  onUserSelectedOccupancy?: (rooms: Array<App.Occupants>) => void
  isMapPage?: boolean
  useMapLayout?: boolean
}

const SearchDesktopInputs = React.forwardRef<HTMLInputElement, Props & MappedStateProps>((props, ref) => {
  const intl = useIntl()
  const locationLabels = useLocationSearchLabels()
  const {
    activeMenu,
    searchItem,
    rooms,
    openTravellerMenu,
    openLocationMenu,
    closeMenu,
    openDatePickerMenu,
    onRoomChange,
    onRoomsApply,
    onDatesApply,
    onDatesDecline,
    showRoomErrors,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    onLocationChange,
    saleUnit,
    typeaheadTypes,
    recentSearches,
    switchView,
    locationSearchPlaceholder = locationLabels.placeholderLabel,
    updateSearchSessionId,
    isLoggedIn,
    label = locationLabels.titleLabel,
    showNaturalLanguageSearchSuggestions = false,
    temporarySearchSubmitRef,
    onUserSelectedDates,
    onUserSelectedOccupancy,
    isMapPage = false,
    useMapLayout = false,
  } = props
  const isMapLayout = isMapPage || useMapLayout
  const dispatch = useAppDispatch()

  const isFormInvalid = useMemo(() => isRoomsInvalid(rooms), [rooms])
  const navigation = useAppNavigation()

  const toListView = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()

    if (switchView) {
      // If you are navigating back from the map view you should be taken back to where you have scrolled
      if (switchView.goBack) {
        navigation.goBack()
        return
      }

      dispatch(pushWithRegion(switchView.URL))
    }
  }, [switchView, navigation, dispatch])

  const hasListViewSwitch = switchView?.view === 'list'

  const inputWrapperRef = useRef<HTMLDivElement>(null)
  const [hasUserInteracted, setHasUserInteracted] = useState({
    dates: false,
    travelers: false,
  })

  const handleDatePickerOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setHasUserInteracted(prev => ({ ...prev, dates: true }))
    setTravelDatesShowAIGlow(false)
    openDatePickerMenu(e)
  }, [openDatePickerMenu])

  const handleTravellerMenuOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setHasUserInteracted(prev => ({ ...prev, travelers: true }))
    setOccupancyShowAIGlow(false)
    openTravellerMenu(e)
  }, [openTravellerMenu])

  const handleRoomChange = useCallback((rooms: Array<App.Occupants>) => {
    setHasUserInteracted(prev => ({ ...prev, travelers: true }))
    setOccupancyShowAIGlow(false)
    onRoomChange(rooms)
    onUserSelectedOccupancy?.(rooms)
  }, [onRoomChange, onUserSelectedOccupancy])

  const handleLocationChange = useCallback((item: App.SearchItem) => {
    // Reset user interactions when location changes
    setHasUserInteracted({
      dates: false,
      travelers: false,
    })
    onLocationChange?.(item)
  }, [onLocationChange])

  const [travelDatesShowAIGlow, setTravelDatesShowAIGlow] = useState(false)
  // Using number trigger starting at 0 to skip effect on mount, increment to trigger glow
  const [travelDatesGlowTrigger, setTravelDatesGlowTrigger] = useState(0)

  useEffect(() => {
    if (travelDatesGlowTrigger === 0) return // Skip initial mount

    setTravelDatesShowAIGlow(true)

    const timer = setTimeout(() => {
      setTravelDatesShowAIGlow(false)
    }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

    return () => clearTimeout(timer)
  }, [travelDatesGlowTrigger])

  const [occupancyShowAIGlow, setOccupancyShowAIGlow] = useState(false)
  // Using number trigger starting at 0 to skip effect on mount, increment to trigger glow
  const [occupancyGlowTrigger, setOccupancyGlowTrigger] = useState(0)

  useEffect(() => {
    if (occupancyGlowTrigger === 0) return // Skip initial mount

    setOccupancyShowAIGlow(true)

    const timer = setTimeout(() => {
      setOccupancyShowAIGlow(false)
    }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

    return () => clearTimeout(timer)
  }, [occupancyGlowTrigger])

  return <Group direction="horizontal" gap={12} horizontalAlign="center" verticalAlign="center">
    {hasListViewSwitch && isMapLayout && <CSSBreakpoint min="tablet">
      <TextButton
        kind="tertiary"
        size="large"
        onClick={toListView}
        className="list-view"
        aria-label={intl.formatMessage({
          id: 'WBjNYbkwGBMPU9zKBSTt',
          defaultMessage: 'list-view',
          description: 'Accessibility aria-label for the list view button in the search interface',
        })}
        data-testid="list-view"
        startIcon={<LineArrowLeftIcon />}
      >
        {switchView.label}
      </TextButton>
    </CSSBreakpoint>}

    <InputWrapper className={clsx({ 'map-view': isMapLayout })} ref={inputWrapperRef}>
      <InputContainer className="location-input">
        <SearchLocationInput
          label={label}
          placeholder={locationSearchPlaceholder}
          placeTypes={typeaheadTypes}
          onChange={handleLocationChange}
          variant="dropdown"
          inputRef={ref}
          recentSearchItems={recentSearches}
          onLocationToggle={openLocationMenu}
          controlsContainerRef={inputWrapperRef}
          searchVertical="hotel"
          updateSearchSessionId={updateSearchSessionId}
          showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
          userInteractions={hasUserInteracted}
          setTravelDatesShowAIGlow={() => setTravelDatesGlowTrigger(prev => prev + 1)}
          setOccupancyShowAIGlow={() => setOccupancyGlowTrigger(prev => prev + 1)}
          temporarySearchSubmitRef={temporarySearchSubmitRef}
        />
      </InputContainer>
      <InputContainer className="date-input">
        <DateInputDesktop
          dropdownAnchorRef={inputWrapperRef}
          onDateToggle={handleDatePickerOpen}
          isToggled={activeMenu === SearchMenuStates.Dates}
          onDatesApply={onDatesApply}
          onDatesDecline={onDatesDecline}
          anytimeDateSelected={anytimeDateSelected}
          selectAnytimeDate={selectAnytimeDate}
          selectSpecificDates={selectSpecificDates}
          selectFlexibleDates={selectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
          closeMenu={closeMenu}
          showAIGlow={travelDatesShowAIGlow}
          onUserDatesChanged={onUserSelectedDates}
        />
      </InputContainer>
      <InputContainer className="traveller-input">
        <SearchTravellersInputDesktop
          dropdownAnchorRef={inputWrapperRef}
          rooms={rooms}
          onTravellersToggle={handleTravellerMenuOpen}
          isToggled={activeMenu === SearchMenuStates.Rooms}
          onRoomChange={handleRoomChange}
          onRoomsApply={onRoomsApply}
          closeMenu={closeMenu}
          isLastStep={!!searchItem}
          error={showRoomErrors && isFormInvalid}
          saleUnit={saleUnit}
          showAIGlow={occupancyShowAIGlow}
        />
      </InputContainer>
      <InputContainer className="actions">
        {!isMapLayout && <SearchButton kind="primary" type="submit" size="large">
          {intl.formatMessage({
            id: '1bC31Z0mNbPihDOwN4pe',
            defaultMessage: 'Search',
            description: 'Search button label on desktop search form in the search interface',
          })}
        </SearchButton>}
        {isMapLayout && <IconButton kind="primary" type="submit" size="large">
          <LineSearchIcon />
        </IconButton>}
      </InputContainer>
    </InputWrapper>
    {isMapPage && <>
      {config.NAVKIT_NAVBAR_ENABLED && <MapSearchNav />}
      {!config.NAVKIT_NAVBAR_ENABLED && <NavigationHeader isLoggedIn={isLoggedIn} />}
    </>}
  </Group>
})

export default connect<MappedStateProps, undefined, Props, App.State>((state: App.State): MappedStateProps => (
  {
    isLoggedIn: selectLoggedIn(state),
  }
), null, null, { forwardRef: true })(SearchDesktopInputs)
